import type { ExistingCompanyLink, AvailableCompany } from '../types'

import sortBy from 'lodash/sortBy'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { SfLog } from '@app/adapters/logger'
import {
  getExistingCompanyLinks,
  getAvailableCompanies,
} from '@app/application/company_links/api'

export const useCompanyLinksStore = defineStore('companyLinks', () => {
  const initialized = ref<boolean>(false)
  const existingCompanyLinks = ref<ExistingCompanyLink[]>([])
  const availableCompanies = ref<AvailableCompany[]>([])
  const companyLinkToDelete = ref<ExistingCompanyLink | null>(null)
  const currentCompanyName = ref<string | null>(null)
  const linkedCompaniesWithPeriod = ref<number[]>([])

  const sortedAvailableCompanies = computed(() => {
    return sortBy(availableCompanies.value, ['name'])
  })

  const sortedExistingCompanyLinks = computed(() => {
    return sortBy(existingCompanyLinks.value, ['sourceCompanyName'])
  })

  async function initExistingCompanyLinks(): Promise<void> {
    try {
      const details = await getExistingCompanyLinks()

      if (!details) {
        return
      }

      existingCompanyLinks.value = details.existingCompanyLinks
      linkedCompaniesWithPeriod.value = details.linkedCompaniesWithPeriod
      initialized.value = true
    } catch (error) {
      SfLog.warn('Failed to load existing company links', {
        extra: {
          error: error,
        },
      })
    }
  }

  async function initAvailableCompanies(): Promise<void> {
    try {
      const details = await getAvailableCompanies()
      if (!details) {
        return
      }

      availableCompanies.value = details.availableCompanies
      currentCompanyName.value = details.currentCompanyName
    } catch (error) {
      SfLog.warn('Failed to load available companies', {
        extra: {
          error: error,
        },
      })
    }
  }

  function addCompanyLink(
    newCompanyLink: ExistingCompanyLink,
    hasMatchingPeriod: boolean
  ): void {
    existingCompanyLinks.value.push(newCompanyLink)

    availableCompanies.value = availableCompanies.value.filter(
      (company) => company.id !== newCompanyLink.sourceCompanyId
    )

    if (
      hasMatchingPeriod &&
      !linkedCompaniesWithPeriod.value.includes(newCompanyLink.sourceCompanyId)
    ) {
      linkedCompaniesWithPeriod.value.push(newCompanyLink.sourceCompanyId)
    }
  }

  function removeCompanyLink(linkId: number): void {
    let removedLink: ExistingCompanyLink | undefined

    existingCompanyLinks.value = existingCompanyLinks.value.filter((link) => {
      if (link.id === linkId) {
        removedLink = link
        return false
      }
      return true
    })

    if (!removedLink) {
      return
    }

    const newAvailableCompany = {
      id: removedLink.sourceCompanyId,
      name: removedLink.sourceCompanyName,
    } as AvailableCompany
    availableCompanies.value.push(newAvailableCompany)

    const sourceCompanyId = removedLink.sourceCompanyId
    if (linkedCompaniesWithPeriod.value.includes(sourceCompanyId)) {
      linkedCompaniesWithPeriod.value = linkedCompaniesWithPeriod.value.filter(
        (companyId) => companyId !== sourceCompanyId
      )
    }
  }

  function markLinkForDeletion(existingCompanyLink: ExistingCompanyLink): void {
    companyLinkToDelete.value = existingCompanyLink
  }

  function clearLinkForDeletion(): void {
    companyLinkToDelete.value = null
  }

  return {
    initialized,
    existingCompanyLinks: sortedExistingCompanyLinks,
    availableCompanies: sortedAvailableCompanies,
    companyLinkToDelete,
    currentCompanyName,
    linkedCompaniesWithPeriod,
    initExistingCompanyLinks,
    initAvailableCompanies,
    addCompanyLink,
    removeCompanyLink,
    markLinkForDeletion,
    clearLinkForDeletion,
  }
})
